import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { Package, Truck, CheckCircle2 } from "lucide-react";

const STAGES = [
    { key: "confirmed", label: "Order confirmed" },
    { key: "packed", label: "Packed discreetly" },
    { key: "in_transit", label: "In transit" },
    { key: "delivered", label: "Delivered" },
];

export default function TrackOrder() {
    const [params, setParams] = useSearchParams();
    const initial = params.get("number") || "";
    const [num, setNum] = useState(initial);
    const [order, setOrder] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const lookup = async (e) => {
        if (e) e.preventDefault();
        if (!num) return;
        setLoading(true);
        setErr("");
        setOrder(null);
        try {
            const { data } = await api.get(`/orders/${num.trim()}`);
            setOrder(data);
            const p = new URLSearchParams(params);
            p.set("number", num.trim().toUpperCase());
            setParams(p);
        } catch {
            setErr("We couldn't find an order with that number.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initial) lookup();
        // eslint-disable-next-line
    }, []);

    return (
        <div
            className="max-w-[900px] mx-auto px-6 md:px-12 pt-16 pb-32"
            data-testid="track-order-page"
        >
            <div className="overline mb-4">Private lookup</div>
            <h1
                className="font-serif text-5xl sm:text-6xl tracking-tight mb-6"
                style={{ color: "var(--vl-text)" }}
            >
                Track your{" "}
                <em
                    style={{
                        color: "var(--vl-rose)",
                        fontStyle: "italic",
                    }}
                >
                    order.
                </em>
            </h1>
            <p
                className="max-w-xl"
                style={{
                    color: "var(--vl-muted)",
                    lineHeight: 1.8,
                }}
            >
                Enter your order number (begins with <em>VLR-</em>). We'll
                never send a confirmation email that reveals what's inside.
            </p>

            <form
                onSubmit={lookup}
                className="mt-10 flex items-end gap-4"
                data-testid="track-form"
            >
                <input
                    value={num}
                    onChange={(e) => setNum(e.target.value.toUpperCase())}
                    placeholder="VLR-XXXXXX"
                    className="vl-input"
                    data-testid="track-input"
                />
                <button
                    type="submit"
                    disabled={loading}
                    className="vl-btn vl-btn-solid"
                    data-testid="track-submit"
                >
                    {loading ? "…" : "Lookup"}
                </button>
            </form>

            {err && (
                <p
                    className="mt-6 text-sm"
                    style={{ color: "var(--vl-rose)" }}
                    data-testid="track-error"
                >
                    {err}
                </p>
            )}

            {order && (
                <div
                    className="mt-14 p-8"
                    style={{
                        background: "var(--vl-surface)",
                        border: "1px solid var(--vl-border)",
                    }}
                    data-testid="track-result"
                >
                    <div className="flex flex-wrap items-center gap-6 mb-8">
                        <div>
                            <div className="overline">Order</div>
                            <div
                                className="font-serif text-2xl mt-1"
                                style={{ color: "var(--vl-text)" }}
                            >
                                {order.order_number}
                            </div>
                        </div>
                        <div>
                            <div className="overline">Placed</div>
                            <div
                                className="mt-1 text-sm"
                                style={{ color: "var(--vl-text)" }}
                            >
                                {new Date(order.created_at).toLocaleString(
                                    "en-IN",
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="overline">Total</div>
                            <div
                                className="mt-1 font-serif text-xl"
                                style={{ color: "var(--vl-text)" }}
                            >
                                {formatINR(order.total)}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-6">
                        {STAGES.map((s, i) => {
                            const active = i === 0; // confirmed by default
                            return (
                                <div key={s.key}>
                                    <div
                                        className="h-[2px]"
                                        style={{
                                            background:
                                                active
                                                    ? "var(--vl-rose)"
                                                    : "var(--vl-border)",
                                        }}
                                    />
                                    <div
                                        className="mt-3 text-[11px] tracking-[0.22em] uppercase"
                                        style={{
                                            color: active
                                                ? "var(--vl-rose)"
                                                : "var(--vl-muted)",
                                        }}
                                    >
                                        {s.label}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div
                        className="hairline my-6"
                        style={{
                            background: "var(--vl-border)",
                            height: 1,
                        }}
                    />
                    <div className="grid sm:grid-cols-3 gap-5">
                        <div>
                            <CheckCircle2
                                size={18}
                                strokeWidth={1.25}
                                color="var(--vl-rose)"
                            />
                            <div
                                className="mt-2 text-sm"
                                style={{ color: "var(--vl-text)" }}
                            >
                                Order confirmed
                            </div>
                            <div
                                className="text-[11px] mt-1"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                Your order is being prepared by our atelier.
                            </div>
                        </div>
                        <div>
                            <Package
                                size={18}
                                strokeWidth={1.25}
                                color="var(--vl-muted)"
                            />
                            <div
                                className="mt-2 text-sm"
                                style={{ color: "var(--vl-text)" }}
                            >
                                Packed discreetly
                            </div>
                            <div
                                className="text-[11px] mt-1"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                Unbranded mailer, sealed, tissue-wrapped.
                            </div>
                        </div>
                        <div>
                            <Truck
                                size={18}
                                strokeWidth={1.25}
                                color="var(--vl-muted)"
                            />
                            <div
                                className="mt-2 text-sm"
                                style={{ color: "var(--vl-text)" }}
                            >
                                On its way
                            </div>
                            <div
                                className="text-[11px] mt-1"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                Handled privately by our courier partner.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
